import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { getAudioContext, playChime } from "@/lib/sound";

export default function Preloader({ onFinish }) {
  const [exiting, setExiting] = useState(false);
  const [gone, setGone] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("cs-sound") !== "off");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      setExiting(true);
      document.body.style.overflow = "";
      onFinish?.();
    }, 2100);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("cs-sound", next ? "on" : "off");
    if (next) {
      const ac = getAudioContext();
      if (ac && ac.state === "suspended") {
        ac.resume().then(() => playChime(0.14, true)).catch(() => playChime(0.14, true));
      } else {
        playChime(0.14, true);
      }
    }
  };

  if (gone) return null;

  return (
    <motion.div
      data-testid="preloader"
      initial={{ y: 0 }}
      animate={{ y: exiting ? "-100%" : 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      onAnimationComplete={() => exiting && setGone(true)}
      className="fixed inset-0 z-[100] bg-[#F7F4EC] flex flex-col items-center justify-center"
    >
      <motion.img
        src="/logo-full.png"
        alt="ClaimSpace LegalTech"
        initial={{ opacity: 0, scale: 0.9, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="w-60 sm:w-80"
      />
      <div className="mt-12 h-px w-52 bg-navy-950/10 overflow-hidden">
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "0%" }}
          transition={{ duration: 1.6, ease: "easeInOut", delay: 0.3 }}
          className="h-full w-full bg-gold"
        />
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
        className="mt-6 text-[10px] uppercase tracking-[0.5em] text-navy-800/70"
      >
        Intellectual Property Rights Consultants
      </motion.p>

      <motion.button
        type="button"
        onClick={toggleSound}
        data-testid="preloader-sound-toggle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        aria-label={soundOn ? "Mute intro sound" : "Unmute intro sound"}
        className="absolute bottom-8 right-8 flex items-center gap-2 text-navy-800/60 hover:text-navy-800 transition-colors duration-300"
      >
        {soundOn ? <SpeakerHigh size={20} weight="duotone" /> : <SpeakerSlash size={20} weight="duotone" />}
        <span className="text-[10px] uppercase tracking-[0.3em]">{soundOn ? "Sound on" : "Sound off"}</span>
      </motion.button>
    </motion.div>
  );
}
